import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { isNativeApp } from '../lib/platform';
import { runningBundleHash, shouldReloadSilently } from '../lib/appUpdateCore';
import { anyDialogOpen, anyMediaPlaying, fetchServedBundleHash } from '../lib/appUpdate';

// List-like screens where a silent reload after a long absence loses nothing.
const SAFE_PATHS = ['/', '/library', '/dashboard'] as const;
const MIN_CHECK_GAP_MS = 5 * 60_000;
const POLL_MS = 30 * 60_000;

/**
 * "A new version is available" (2026-10-04): when the tab or app comes back (and every 30 min
 * while visible) compare our main bundle hash with the one the server's index.html names.
 * Both servers build identical files from the same commit, so a difference means a deploy.
 */
export default function UpdateWatcher() {
  const { t } = useTranslation();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const running = runningBundleHash(document);
    if (!running) return; // dev server / tests: nothing hashed to compare
    let hiddenAt: number | null = null;
    let lastCheck = Date.now();
    let notified: string | null = null;

    const check = async (hiddenForMs: number) => {
      lastCheck = Date.now();
      const served = await fetchServedBundleHash();
      if (!served || served === running || served === notified) return;
      if (shouldReloadSilently({
        hiddenForMs,
        pathname: pathRef.current,
        safePaths: SAFE_PATHS,
        dialogOpen: anyDialogOpen(),
        mediaPlaying: anyMediaPlaying(),
      })) {
        window.location.reload();
        return;
      }
      notified = served;
      toast(tRef.current('app.updateAvailable'), {
        id: 'app-update',
        duration: Infinity,
        action: { label: tRef.current('app.updateReload'), onClick: () => window.location.reload() },
      });
    };

    const onHidden = () => { if (hiddenAt === null) hiddenAt = Date.now(); };
    const onVisible = () => {
      const hiddenForMs = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (Date.now() - lastCheck >= MIN_CHECK_GAP_MS || hiddenForMs >= MIN_CHECK_GAP_MS) void check(hiddenForMs);
    };
    const onVisibility = () => (document.visibilityState === 'hidden' ? onHidden() : onVisible());
    document.addEventListener('visibilitychange', onVisibility);
    const poll = window.setInterval(() => {
      if (document.visibilityState === 'visible') void check(0);
    }, POLL_MS);

    // The shells: pause/resume from the App plugin as well — the WebView's visibility events are not reliable there.
    let cancelled = false;
    const handles: { remove: () => Promise<void> }[] = [];
    if (isNativeApp() && Capacitor.isPluginAvailable('App')) {
      import('@capacitor/app')
        .then(async ({ App: CapApp }) => {
          const hs = [await CapApp.addListener('pause', onHidden), await CapApp.addListener('resume', onVisible)];
          if (cancelled) hs.forEach((h) => h.remove()); else handles.push(...hs);
        })
        .catch(() => { /* plugin unavailable */ });
    }

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(poll);
      handles.forEach((h) => h.remove());
    };
  }, []);

  return null;
}
